import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet } from "@/lib/api";
import { Users, Rss, Brain, Mic, CheckCircle2, Activity, ArrowRight } from "lucide-react";

export default function WorkspaceOverview() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    apiGet(`/workspaces/${workspaceId}`).then(setWs);
    apiGet(`/workspaces/${workspaceId}/overview`).then(setOverview);
  }, [workspaceId]);

  const stats = [
    { label: "contacts", value: overview?.contacts ?? 0, icon: Users, to: "contacts" },
    { label: "pending drafts", value: overview?.pending_drafts ?? 0, icon: Rss, to: "feed" },
    { label: "engagements", value: overview?.engagements ?? 0, icon: CheckCircle2 },
    { label: "voice samples", value: overview?.voice_samples ?? 0, icon: Mic, to: "context-brain" },
    { label: "ICP signals", value: overview?.icp_signals ?? 0, icon: Brain, to: "context-brain" },
    { label: "posts tracked", value: overview?.posts ?? 0, icon: Activity },
  ];

  return (
    <Layout title={ws?.name || "Workspace"}>
      <div className="mb-8 max-w-2xl">
        {ws?.product && (
          <div className="text-sm text-zinc-400 mb-2">
            <span className="overline mr-2" style={{ fontSize: 9 }}>
              product
            </span>
            {ws.product}
          </div>
        )}
        {ws?.icp_description && (
          <p className="text-sm text-zinc-400 leading-relaxed">{ws.icp_description}</p>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {stats.map((s, idx) => (
          <button
            key={s.label}
            data-testid={`overview-stat-${s.label.replace(/\s+/g, "-")}`}
            onClick={() => s.to && navigate(`/workspace/${workspaceId}/${s.to}`)}
            className={`surface p-4 text-left fade-up ${s.to ? "surface-hover" : ""}`}
            style={{ animationDelay: `${idx * 50}ms` }}
          >
            <div className="flex items-center justify-between mb-2">
              <s.icon className="w-3.5 h-3.5 text-zinc-500" />
              {s.to && <ArrowRight className="w-3 h-3 text-zinc-600" />}
            </div>
            <div className="heading text-3xl text-zinc-100 tracking-tight">{s.value}</div>
            <div className="overline mt-1" style={{ fontSize: 9 }}>
              {s.label}
            </div>
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ActionCard
          title="Build the brain"
          desc="Add voice samples, ICP signal map, knowledge, features and pain points so the AI writes like the brand owner."
          cta="Open Context Brain"
          onClick={() => navigate(`/workspace/${workspaceId}/context-brain`)}
          testid="cta-context-brain"
        />
        <ActionCard
          title="Run the feed"
          desc="Add target contacts, paste posts (or wait for scrape), review and use AI-drafted comments."
          cta="Open Feed"
          onClick={() => navigate(`/workspace/${workspaceId}/feed`)}
          testid="cta-feed"
        />
      </div>
    </Layout>
  );
}

function ActionCard({ title, desc, cta, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="surface surface-hover p-6 text-left group fade-up"
    >
      <div className="heading text-xl text-zinc-100 mb-2">{title}</div>
      <p className="text-sm text-zinc-400 leading-relaxed mb-4">{desc}</p>
      <div className="text-sm text-zinc-200 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
        {cta} <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}
